import type { Infer } from 'spacetimedb';
import { MyMapEventsRow, type ErrorContext } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { Logger } from '../types';
import { coerceMapEventType, type MapEventData } from '../mapEvents';

type MyMapEventsTableRow = Infer<typeof MyMapEventsRow>;

export class MapEventModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly logger: Logger = () => undefined
  ) {}

  public async subscribeToMapEvents(
    onUpdate: (events: MapEventData[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const eventsView = connection.db.myMapEvents;

    if (!eventsView) {
      this.logger('my_map_events view not available yet - bindings need regeneration');
      return { unsubscribe: () => {} };
    }

    const seenEventIds = new Set<string>();
    let readyForIncremental = false;

    const toMapEvent = (row: MyMapEventsTableRow): MapEventData => ({
      eventId: row.eventId,
      eventType: coerceMapEventType(row.eventType),
      instanceId: row.instanceId,
      mapId: row.mapId,
      sourceId: row.sourceId,
      targetId: row.targetId,
      skillId: row.skillId,
      amount: Number(row.amount ?? 0),
      targetHp: Number(row.targetHp ?? 0),
      targetMaxHp: Number(row.targetMaxHp ?? 0),
      sourceX: Number(row.sourceX ?? 0),
      sourceY: Number(row.sourceY ?? 0),
      targetX: Number(row.targetX ?? 0),
      targetY: Number(row.targetY ?? 0),
      snap: Boolean((row as MyMapEventsTableRow & { snap?: boolean | null }).snap),
      payloadJson: String((row as MyMapEventsTableRow & { payloadJson?: string | null }).payloadJson ?? ''),
      tick: Number(row.tick ?? 0),
      createdAt: typeof row.createdAt?.toDate === 'function' ? row.createdAt.toDate() : new Date(0),
    });

    const handleInsert = (_ctx: unknown, row: MyMapEventsTableRow): void => {
      if (!readyForIncremental) {
        return;
      }
      const mapped = toMapEvent(row);
      if (seenEventIds.has(mapped.eventId)) {
        return;
      }
      seenEventIds.add(mapped.eventId);
      onUpdate([mapped]);
    };

    const handleDelete = (_ctx: unknown, row: MyMapEventsTableRow): void => {
      const eventId = row?.eventId;
      if (!eventId) {
        return;
      }
      seenEventIds.delete(eventId);
    };

    eventsView.onInsert(handleInsert);
    eventsView.onDelete(handleDelete);

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_map_events';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) {
          return;
        }
        listenersRemoved = true;
        eventsView.removeOnInsert(handleInsert);
        eventsView.removeOnDelete(handleDelete);
      };

      builder
        .onApplied(() => {
          try {
            for (const row of eventsView.iter()) {
              const eventId = row.eventId;
              if (eventId) {
                seenEventIds.add(eventId);
              }
            }
            readyForIncremental = true;
            onUpdate([]);
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Map events subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }
}
